/**
 * Where the parts of the installation are.
 *
 * An installation is either at one address, which holds everything (a developer's
 * machine), or on hosts of its own: the front page and the way in at the domain
 * itself, learning and the studio at `app.`, moderation and the accounts at
 * `admin.`, what developers read at `dev.`. The server says which (`GET /site`),
 * once, before anything is shown.
 *
 * One build serves every host: which pages there are follows from the host the
 * page was opened at (app/router.tsx). A link to a page of another part is a whole
 * address; a link within the part is a path, as it always was.
 */
export const PARTS = ['home', 'app', 'admin', 'dev'] as const;
export type Part = (typeof PARTS)[number];

export interface Site extends Record<Part, string> {
  /** Each part has a host of its own. Otherwise the four addresses are one. */
  subdomains: boolean;
}

const oneAddress = (): Site => {
  const here = globalThis.location.origin;
  return { subdomains: false, home: here, app: here, admin: here, dev: here };
};

let site: Site = oneAddress();

/** Say where the parts are. `null`: at this one address, all of them. */
export function setSite(next: Site | null): void {
  site = next ?? oneAddress();
}

/**
 * Ask the server where the parts are. If it cannot be asked, the page goes on as
 * if everything were here: nothing it shows depends on the answer for safety, the
 * server checks every request.
 */
export async function loadSite(): Promise<void> {
  try {
    const response = await fetch(new Request(new URL('/api/v1/site', globalThis.location.origin)));
    setSite(response.ok ? ((await response.json()) as Site) : null);
  } catch {
    setSite(null);
  }
}

const originOf = (url: string) => new URL(url).origin;

/** The part this page is in; `null` when one address holds them all. */
export function partHere(): Part | null {
  if (!site.subdomains) return null;
  const here = globalThis.location.origin;
  // A host the server did not name can only show the way in.
  return PARTS.find((part) => originOf(site[part]) === here) ?? 'home';
}

/** The domain every host of the installation is under; `null` at one address. */
export const baseDomain = (): string | null =>
  site.subdomains ? new URL(site.home).hostname : null;

/** Whether pages of this part are at the address the page was opened at. */
export const isHere = (part: Part): boolean => {
  const here = partHere();
  return here === null || here === part;
};

/** A link to a page of a part: a path when the part is here, a whole address otherwise. */
export const urlOf = (part: Part, path = '/'): string =>
  isHere(part) ? path : `${site[part].replace(/\/$/, '')}${path}`;

/** What is done with what visitors leave: a static page beside the front page (frontend/site). */
export const privacyPageUrl = (locale: string): string => urlOf('home', `/${locale}/privacy/`);

/** Whether an address belongs to this installation: a path here, or a page of one of its hosts. */
export function isOwnUrl(url: string): boolean {
  if (url.startsWith('/')) return !url.startsWith('//');
  try {
    const origin = originOf(url);
    return PARTS.some((part) => originOf(site[part]) === origin);
  } catch {
    return false;
  }
}

/** Whether a link leads away from this page's host, so the router cannot follow it. */
export const isElsewhere = (url: string): boolean => !url.startsWith('/');

/**
 * Leaving the page for another host. An object, so that tests can watch it instead
 * of letting the test browser try to navigate.
 */
export const browser = {
  leave(url: string): void {
    globalThis.location.assign(url);
  },
};
